<template>
  <div class="w-full h-full bg-gray-900 flex relative">
    <!-- sidebar -->
    <Sidebar :currentStep="currentStep" />

    <!-- main content -->
    <div
      v-if="user"
      class="w-full h-full flex lg:justify-center lg:items-center z-10 mt-[58px] lg:mt-0 lg:ml-[320px]"
    >
      <div
        class="w-full h-full flex lg:justify-center relative px-4 py-8 lg:py-0 lg:px-0"
      >
        <WelcomePage
          @updateOnboardingStep="updateOnboardingStep"
          :currentStep="currentStep"
          v-if="currentStep === 0"
        />
        <ConnectAccountsPage
          @updateOnboardingStep="updateOnboardingStep"
          :currentStep="currentStep"
          v-else-if="currentStep === 1"
        />
        <SteamBotPage
          @updateOnboardingStep="updateOnboardingStep"
          :currentStep="currentStep"
          v-else-if="currentStep === 2"
        />
        <MatchHistoryPage
          @updateOnboardingStep="updateOnboardingStep"
          :currentStep="currentStep"
          v-else-if="currentStep === 3"
        />
        <FreeTrialPage
          @updateOnboardingStep="updateOnboardingStep"
          :currentStep="currentStep"
          :overwriteTrialCookie="overwriteTrialCookie"
          @updateOverwriteTrialCookie="updateOverwriteTrialCookie"
          v-else-if="currentStep === 4 && trialCookie && !overwriteTrialCookie"
        />
        <SubscribePage
          @updateOnboardingStep="updateOnboardingStep"
          :currentStep="currentStep"
          v-else-if="currentStep === 4"
        />
        <PlayNowPage
          @updateOnboardingStep="updateOnboardingStep"
          @finishOnboarding="finishOnboarding"
          :currentStep="currentStep"
          v-else-if="currentStep === 5"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import {
  ref, computed, onMounted, watch,
} from 'vue';
import axios from 'axios';
import { useRouter, useRoute } from 'vue-router';
import { useGtm } from '@gtm-support/vue-gtm';
import { useHead } from '@unhead/vue';
import Sidebar from '../components/Onboarding/Sidebar.vue';
import { clearSignedOutUser } from '../utils/session';
import WelcomePage from '../components/Onboarding/WelcomePage.vue';
import SubscribePage from '../components/Onboarding/SubscribePage.vue';
import ConnectAccountsPage from '../components/Onboarding/ConnectAccountsPage.vue';
import PlayNowPage from '../components/Onboarding/PlayNowPage.vue';
import SteamBotPage from '../components/Onboarding/SteamBotPage.vue';
import FreeTrialPage from '../components/Onboarding/FreeTrialPage.vue';
import MatchHistoryPage from '../components/Onboarding/MatchHistoryPage.vue';
import { onboardingStepName, onboardingStepVariant, trackOnboarding } from '../utils/onboardingAnalytics';

const { t } = useI18n();
const auth = useAuth();
const user = ref(auth.user());
const currentStep = ref(user.value.onboarded_steps_completed);
const router = useRouter();
const route = useRoute();
const gtm = useGtm();
const trialCookie = ref(null);
const overwriteTrialCookie = ref(false);
const stepVariant = computed(() => onboardingStepVariant(currentStep.value, {
  trialCookie: trialCookie.value,
  overwriteTrialCookie: overwriteTrialCookie.value,
}));

function trackStepView() {
  trackOnboarding('onboarding_step_viewed', {
    step_number: currentStep.value,
    step_name: onboardingStepName(currentStep.value),
    step_variant: stepVariant.value,
    steam_linked: Boolean(user.value.steam_id),
  });
}

function getTrialCookieValue() {
  if (typeof route.params.source === 'string' && route.params.source.trim()) {
    return route.params.source.trim();
  }

  const value = document.cookie
    .split('; ')
    .find((row) => row.startsWith('trial='))
    ?.split('=')[1];

  if (!value || value === 'true') return null;

  return decodeURIComponent(value);
}

onMounted(() => {
  if (gtm && currentStep.value === 0) {
    gtm.push({ event: 'tutorial_begin' });
  }

  // Check for trial cookie
  trialCookie.value = getTrialCookieValue();

  // A view on every mount (returning users, the return from Steam), then on every step or
  // step 4 variant change, Back and the update_user rollback included. Sync, so none coalesce.
  trackStepView();
  watch([currentStep, stepVariant], trackStepView, { flush: 'sync' });
});
useHead({
  title: t('onboarding.page.title'),
});

function logout() {
  clearSignedOutUser();
  auth.logout();
}

function updateOverwriteTrialCookie(value) {
  overwriteTrialCookie.value = value;
}

function updateOnboardingStep(data) {
  const previousStep = currentStep.value;
  const newStep = data.value;

  // Track individual onboarding steps
  if (gtm && newStep !== previousStep) {
    gtm.push({
      event: 'onboarding_step_completed',
      step_number: previousStep,
      step_name: onboardingStepName(previousStep),
      next_step: newStep,
    });
  }

  currentStep.value = newStep;
  axios({
    url: '/users/update_user',
    method: 'PUT',
    data: {
      onboarded_steps_completed: newStep,
    },
  }).catch(() => {
    currentStep.value = user.value.onboarded_steps_completed;
  });
}

function finishOnboarding() {
  axios({
    url: '/users/update_user',
    method: 'PUT',
    data: {
      onboarded_steps_completed: 5,
    },
  }).then(() => {
    gtm.push({ event: 'tutorial_complete' });
    currentStep.value = 5;
    auth.fetch();

    if (Object.keys(route.query).length > 0) {
      router.push({
        name: 'Redeem',
        query: route.query,
      });
    } else {
      router.push({ name: 'Dashboard' });
    }
  });
}
</script>
